import useComponentSize from '@rehooks/component-size';
import { Dictionary, keyBy } from 'lodash';
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Layout } from 'react-grid-layout';
import { useDispatch, useSelector } from 'react-redux';

import {
  useAvailableWidgets,
  useWorkspaceSettings,
} from '~/scenes/MyWorkspace/hooks';

import { updateWidgetsOrder, updateWidgetsSize } from '../../layoutSlice';
import { myWorkspaceLayoutStoreStateSelector } from '../../selectors';
import {
  WidgetSettings,
  WorkspaceId_NO_WORKSPACE,
  WorkspaceSettings,
} from '../../userSettings';

import { getActiveLayoutBreakpoint } from './activeLayoutBreakpoint';
import { buildWorkspaceLayout } from './buildWorkspaceLayout';
import { parseWorkspaceLayoutOrder } from './parseWorkspaceLayoutOrder';
import { parseWorkspaceLayoutSizes } from './parseWorkspaceLayoutSizes';

export interface LayoutData {
  layout: Layout[];
  settings: Dictionary<WidgetSettings>;
}

export interface LayoutSettings {
  cols: number;
  canReorder: boolean;
  canResize: boolean;
}

export interface UseManageWorkspaceLayoutsOutput {
  activeWorkspace: WorkspaceSettings;
  containerRef: RefObject<HTMLDivElement>;
  containerWidth: number;
  layoutSettings: LayoutSettings;
  layoutData?: LayoutData;
  manageWorkspace?: WorkspaceSettings;
  handleDragStop: (nextLayout: Layout[]) => void;
  handleResizeStop: (nextLayout: Layout[]) => void;
  hasNoWorkspace: boolean;
  workspaces: WorkspaceSettings[];
  isWidgetAvailable: (widget: WidgetSettings) => boolean;
}

export const useManageWorkspaceLayouts =
  (): UseManageWorkspaceLayoutsOutput => {
    const { activeWorkspace, workspaces, activeWorkspaceRole } =
      useWorkspaceSettings();

    const hasNoWorkspace = activeWorkspace.id === WorkspaceId_NO_WORKSPACE;

    const containerRef = useRef<HTMLDivElement>(null);
    const { width: containerWidth } = useComponentSize(containerRef);

    const [layoutSettings, setLayoutSettings] = useState<LayoutSettings>({
      cols: 0,
      canReorder: false,
      canResize: false,
    });

    const dispatch = useDispatch();

    const { manageWorkspace } = useSelector(
      myWorkspaceLayoutStoreStateSelector,
    );

    const availableWidgets = useAvailableWidgets(
      !!manageWorkspace?.restrictToRoles
        ? manageWorkspace.restrictToRoles
        : [activeWorkspaceRole],
    );

    const applyWorkspaceSettingsToWidgets = useCallback(
      (widgets: WidgetSettings[]) => {
        return !!activeWorkspaceRole
          ? widgets.map((widget) => {
              return {
                ...widget,
                config: { ...widget.config, role: activeWorkspaceRole },
              };
            })
          : widgets;
      },
      [activeWorkspaceRole],
    );

    const viewLayoutData: LayoutData | undefined = useMemo(() => {
      if (!layoutSettings.cols) {
        return;
      }

      const visibleWidgets = activeWorkspace.widgets.filter(
        ({ isVisible, widget }) =>
          isVisible && availableWidgets.includes(widget),
      );
      return {
        layout: buildWorkspaceLayout(layoutSettings.cols, visibleWidgets),
        settings: keyBy(
          applyWorkspaceSettingsToWidgets(activeWorkspace.widgets),
          'id',
        ),
      };
    }, [
      availableWidgets,
      layoutSettings.cols,
      activeWorkspace.widgets,
      applyWorkspaceSettingsToWidgets,
    ]);

    const editLayoutData: LayoutData | undefined = useMemo(() => {
      if (!manageWorkspace || !layoutSettings.cols) {
        return;
      }

      return {
        layout: buildWorkspaceLayout(
          layoutSettings.cols,
          manageWorkspace.widgets,
        ),
        settings: keyBy(
          applyWorkspaceSettingsToWidgets(manageWorkspace.widgets),
          'id',
        ),
      };
    }, [layoutSettings.cols, manageWorkspace, applyWorkspaceSettingsToWidgets]);

    const handleDragStop = useCallback(
      (nextLayout: Layout[]) => {
        dispatch(
          updateWidgetsOrder(
            parseWorkspaceLayoutOrder(layoutSettings.cols, nextLayout),
          ),
        );
      },
      [dispatch, layoutSettings.cols],
    );

    const handleResizeStop = useCallback(
      (nextLayout: Layout[]) => {
        dispatch(updateWidgetsSize(parseWorkspaceLayoutSizes(nextLayout)));
      },
      [dispatch],
    );

    useEffect(() => {
      if (containerWidth) {
        const nextLayoutBreakpoint = getActiveLayoutBreakpoint(containerWidth);

        setLayoutSettings((currentLayoutSettings) => {
          if (nextLayoutBreakpoint.cols === currentLayoutSettings.cols) {
            return currentLayoutSettings;
          }

          return nextLayoutBreakpoint;
        });
      }
    }, [containerWidth, setLayoutSettings]);

    const isWidgetAvailable = (widget: WidgetSettings) => {
      return availableWidgets.includes(widget.widget);
    };

    return {
      activeWorkspace,
      containerRef,
      containerWidth,
      layoutSettings,
      layoutData: editLayoutData || viewLayoutData,
      manageWorkspace,
      handleDragStop,
      handleResizeStop,
      hasNoWorkspace,
      workspaces,
      isWidgetAvailable,
    };
  };
